/**
 * API Tag Constants
 *
 * Centralized tag type definitions for RTK Query cache invalidation.
 * Use these constants instead of hardcoded strings for type safety and consistency.
 */

export const API_TAGS = {
  // Auth & Core
  AUTH: {
    INFO: "auth-info",
    USER: "user",
    WORKSPACE: "workspace",
    SINGLE_WORKSPACE: "singleWorkspace",
    GENERAL: "general",
    PERMISSIONS: "permissions",
    INFO_LEGACY: "info",
    NOTIFICATION: "notification",
    ACCOUNTS: "accounts",
    TAX_RATES: "taxRates",
  },
  ACTIVITY: {
    BASE: "Activity",
  },
  // Admin
  ADMIN: {
    BASE: "admin",
    SINGLE: "single-admin",
  },

  // Employee & Payroll
  EMPLOYEE: {
    BASE: "employee",
    PAYROLL_DETAILS: "employee-payroll-details",
    TIME_SHEET: "employee-time-sheet",
    PAYROLL_APPROVAL: "employee-payroll-approval-details",
    OPTIONS: "employeeOptions",
  },

  // Project Register
  PROJECT_REGISTER: {
    LEADS: "leads",
    SINGLE_LEAD: "single-lead",
    OPTIONS: "options",
    ESTIMATE: "estimate",
    SINGLE_ESTIMATE: "single-estimate",
    DRAFTS: "drafts",
  },

  // Projects
  PROJECTS: {
    BASE: "projects",
    PROJECT: "project",
    SINGLE: "single-project",
    SINGLE_LEGACY: "singleProject",
    DRIVE: "project-drive",
    VARIATIONS: "projectVariations",
    ASSIGN_TOOLS: "assignTools",
    SINGLE_ASSIGN_TOOLS: "single-assignTools",
    DAILY_REPORT_PROJECTS: "dailyReportProjects",
    MINIMAL: "minimal-projects",
  },

  // Clients
  CLIENTS: {
    CUSTOMER: "customer",
    CUSTOMER_USER: "customer-user",
    PROSPECT: "prospect",
    PROSPECT_USER: "prospectUser",
    SPECIAL_RATE: "specialRate",
    SINGLE_PROSPECT: "single-prospect",
    CUSTOMER_USER_POSITION: "customer-user-position",
    PROSPECT_USER_POSITION: "prospect-user-position",
  },

  // Reports
  REPORTS: {
    BASE: "reports",
    DAILY: "daily-reports",
    ROSTER_DAILY: "roster-daily-report",
    SINGLE_ROSTER_DAILY: "single-roster-daily-report",
    TIPPING: "tipping-report",
    SINGLE_TIPPING: "single-tipping-report",
    QUESTIONS: "report-questions",
    REPORT_QUESTIONS: "reportQuestions",
    WASTE: "waste-report",
    ONGOING: "ongoing",
    COMPLETED: "completed",
    ARCHIVE: "archive",
    WASTE_MANAGEMENT_SUMMARY: "waste-management-summary",
    REPORT_SETTINGS_THEME_PRESETS: "report-settings-theme-presets",
    REPORT_SETTINGS_ASSETS: "report-settings-assets",
    REPORT_SETTINGS_CONFIG: "report-settings-config",
  },

  // Time Tracking
  TIME_TRACKING: {
    TIMESHEET: "timesheet",
    SETTINGS: "timesheet-settings",
    SHIFTS: "shifts",
    BREAK: "break",
    HOLIDAY: "holiday",
    DAILY_PROJECT: "timesheet-daily-project",
    SUBCONTRACTOR_TIME_SHEET: "subcontractor-time-sheet",
    REPORT_WORK_HOURS: "getReportWithWorkHoursData",
  },

  // Roster
  ROSTER: {
    BASE: "roster",
    SHIFT: "roster-shift",
    DAY_SHEET: "daySheet",
    DAY_SHEET_DRIVERS: "daySheetDrivers",
    DAY_SHEET_ASSIGNED_EMPLOYEES: "daySheetAssignedEmployees",
    DAY_SHEET_EMPLOYEE: "daySheetEmployee",
    DAY_SHEET_SUB_CONTRACTOR_EMPLOYEE: "daySheetSubContractorEmployee",
    SINGLE_DAY_SHEET: "singleDaySheet",
    ALLOCATED: "allocated",
    FORCE_CLOSE_DETAILS: "forceCloseDetails",
    ACTIVE_PROJECT_SUPERVISORS: "activeProjectSupervisors",
  },

  // Invoicing
  INVOICING: {
    BASE: "invoices",
    INVOICE: "invoice",
    CONTRACT: "contract-invoice",
    DOCKET: "docket-invoice",
    DOCKET_LEGACY: "docketInvoicing",
    CONTRACT_LEGACY: "contractInvoicing",
    INVOICED_CONTRACT: "invoicedContract",
    XERO: "xero",
    CLAIM_TYPE: "claimType",
    SINGLE_CLAIMS: "single-claims",
    PAST_CLAIMS: "past-claims",
    PAST_CLAIMS_ACTIVITY: "past-claims-activity",
  },

  // Workforce
  WORKFORCE: {
    EMPLOYEE: "employee",
    MANAGER: "manager",
    SINGLE_MANAGER: "single-manager",
    SUB_CONTRACTOR: "sub-contractor",
    SUBCONTRACTOR: "subcontractor",
    SUBCONTRACTOR_EMPLOYEES: "subcontractor_employees",
    SUBCONTRACTOR_EMPLOYEE: "subcontractor_employee",
    SETTINGS: "workforce-settings",
    WORKFORCE_SETTINGS: "workforceSettings",
  },

  // Fleet
  FLEET: {
    BASE: "fleet",
    SINGLE: "singleFleet",
    OPTIONS: "fleetOptions",
    VEHICLES: "vehicles",
  },

  // Waste Management
  WASTE: {
    SUMMARY: "waste-summary",
    SETTINGS: "waste-settings",
    TYPE: "waste-type",
    SINGLE_TYPE: "single-waste-type",
    REQUEST: "waste-request",
    NOTE: "waste-request-note",
    OVERLAPPING_REQUEST: "overlapping-waste-request",
    SINGLE_REQUEST: "single-waste-request",
    RELEASE_REQUEST: "waste-release-request",
    SUMMARY_LEGACY: "summary",
    WEIGH_BRIDGE: "weigh-bridge",
    WEIGH_BRIDGE_LEGACY: "weighBridge",
    SINGLE_WEIGH_BRIDGE: "single-weighBridge",
    WEIGH_SCALE: "weigh-scale",
    WEIGH_SCALE_LEGACY: "weighScale",
    SINGLE_WEIGH_SCALE: "single-weighScale",
    TIPPING_YARD: "tipping-yard",
    TIPPING_YARD_LEGACY: "tipping-yard",
    SINGLE_TIPPING_YARD: "single-tipping-yard",
    LOAD_TYPE: "load-type",
    SINGLE_LOAD_TYPE: "single-load-type",
    VEHICLE: "vehicle",
    SINGLE_VEHICLE: "single-vehicle",
    FORMAT_SETTING: "format-setting",
  },

  // Warehousing
  WAREHOUSING: {
    BASE: "warehousing",
    ITEMS: "warehouseItems",
    ACTIVE_PROJECTS: "warehouseActiveProjects",
    ASSIGN_HISTORY: "warehouseAssignHistory",
    DETAIL_ITEMS: "warehousingDetailItems",
    DETAIL_PROJECTS: "warehousingDetailProjects",
    DETAIL_HISTORY: "warehousingDetailsAssignHistory",
  },

  // Tools
  TOOLS: {
    BASE: "tools",
    PRODUCTS: "products",
    SINGLE_PRODUCT: "single-product",
    CONSUMED: "consumedProducts",
    SINGLE_SUB_PRODUCT: "single-subProduct",
    SUB_PRODUCT_ITEMS: "subProductItems",
    SINGLE_SUB_PRODUCT_ITEM: "single-subProductItem",
    SUB_PRODUCT_LIST: "subProductList",
    TRANSFER: "transferTools",
    RETURN_REQUEST: "returnRequest",
    AWAITING_REQUEST: "awaitingRequest",
    PRODUCT_REQUEST: "productRequest",
  },

  // Calendar
  CALENDAR: {
    BASE: "calendar",
  },

  // Messages & Activity
  MESSAGES: {
    BASE: "messages",
    ACTIVITY: "activity",
    ACTIVITY_LEGACY: "Activity",
    SOCKET_CHANGE: "socket-change",
    SINGLE_USER: "single-user",
    SINGLE_GROUP: "single-group",
    GROUP: "group",
    CHAT_USERS: "chat-users",
    USER_CHAT: "user-chat",
  },

  // Support
  SUPPORT: {
    BASE: "support",
    TEAM_CONTACT: "teamContact",
    IMPORTANT_DOCUMENT: "importantDocument",
  },

  // Documents
  DOCUMENTS: {
    PARSER: "document-parser",
    BASE: "documents",
    SINGLE: "single-document",
    DRIVE: "drive",
    UPLOADED: "UploadedDocs",
    SINGLE_DOC: "singleDoc",
    PDF_HASH: "PdfHash",
    SINGLE_INVOICE: "SingleInvoice",
  },

  // OHS
  OHS: {
    BASE: "ohs",
    INDUCTION: "induction",
  },

  // Claims
  CLAIMS: {
    BASE: "claims",
    VARIATIONS: "claims-variations",
  },

  // Items
  ITEMS: {
    BASE: "items",
    GROUP: "group",
  },

  // Docket
  DOCKET: {
    BASE: "docket",
    SINGLE: "single-docket",
    VERIFY: "verify-docket",
  },

  // Payroll
  PAYROLL: {
    BASE: "payroll",
    PART_1: "part-1",
  },

  // Users
  USERS: {
    BASE: "users",
  },

  // Xero
  XERO: {
    BASE: "xero",
    TRACKING: "tracking",
    CONTACTS: "contacts",
    ACCOUNTS: "accounts",
    TAX_RATES: "taxRates",
    INVOICES: "invoices",
  },

  // Customer Portal
  CUSTOMER_PORTAL: {
    WAREHOUSE: "c-warehouse",
    WASTE: "c-waste",
    DASHBOARD: "c-dashboard",
    PROJECT: "c-project",
    INVOICE: "c-invoice",
    DOCKET: "c-docket",
    SUPPORT: "c-support",
    USER_MANAGEMENT: "c-user-management",
    DASHBOARD_BASE: "dashboard",
    DASHBOARD_FINANCE: "dashboard-finance",
    WASTE_REPORT: "waste-report",
  },

  // Maintenance
  MAINTENANCE: {
    CURRENT: "maintenance-current",
    WINDOWS: "maintenance-windows",
    SINGLE: "maintenance-single",
  },

  // Cookie Consent
  CONSENT: {
    ME: "consent-me",
  },
} as const;

// Helper function to extract all values from nested object
const extractAllValues = <T extends Record<string, unknown>>(
  obj: T,
): string[] => {
  return Object.values(obj).flatMap((value) =>
    typeof value === "object" && value !== null
      ? extractAllValues(value as Record<string, unknown>)
      : [value as string],
  );
};

// Flat array of all tag types for baseApi configuration (auto-generated from API_TAGS)
export const ALL_API_TAGS = [...new Set(extractAllValues(API_TAGS))] as const;

export { baseApi } from "@/app/api/baseApi";
